import {
  listAdminUser as adminUsersApi,
  listSettings as settingsApi,
  updateSettings as updateSettingApi,
} from '@api/generated/sdk.gen';
import { getConfig } from '@shared/lib/config';

import type { DataProvider } from '@refinedev/core';

type ResourceName = 'settings' | 'users';

function isSupported(resource: string): resource is ResourceName {
  return resource === 'settings' || resource === 'users';
}

/**
 * Data-провайдер Refine.
 *
 * Делегирует вызовы сгенерированному SDK. Сейчас поддерживает:
 * - `settings` — list + getOne + update (через `/api/admin/settings`).
 * - `users` — list с пагинацией (через `/api/admin/users`).
 *
 * Остальные операции (create/delete/custom) кидают ошибку — добавим
 * под конкретный use-case, когда понадобится.
 */
export const dataProvider: DataProvider = {
  getApiUrl: () => getConfig().apiBaseUrl || '/',

  getList: (async ({
    resource,
    pagination,
    sorters,
  }: {
    resource: string;
    pagination?: { currentPage?: number; pageSize?: number };
    sorters?: { field: string; order: 'asc' | 'desc' }[];
  }) => {
    if (!isSupported(resource)) {
      return { data: [], total: 0 };
    }
    if (resource === 'settings') {
      const res = await settingsApi();
      const items = (res.data ?? []).map((s) => ({ id: s.key ?? '', ...s }));
      return { data: items, total: items.length };
    }
    const page = (pagination?.currentPage ?? 1) - 1;
    const size = pagination?.pageSize ?? 20;
    const sort = (sorters ?? []).map((s) => `${s.field},${s.order}`);
    const res = await adminUsersApi({ query: { page, size, sort } });
    const data = res.data;
    return {
      data: (data?.content ?? []).map((u) => ({ id: u.id ?? '', ...u })),
      total: data?.totalElements ?? 0,
    };
  }) as DataProvider['getList'],

  getOne: (async ({ resource, id }: { resource: string; id: string | number }) => {
    if (resource !== 'settings') {
      throw new Error(`getOne not supported for resource "${resource}"`);
    }
    const res = await settingsApi();
    const found = (res.data ?? []).find((s) => s.key === String(id));
    if (!found) throw new Error('SETTING_NOT_FOUND');
    return { data: { id: found.key ?? '', ...found } };
  }) as DataProvider['getOne'],

  update: (async ({
    resource,
    id,
    variables,
  }: {
    resource: string;
    id: string | number;
    variables: { value: string };
  }) => {
    if (resource !== 'settings') {
      throw new Error(`update not supported for resource "${resource}"`);
    }
    const res = await updateSettingApi({ path: { key: String(id) }, body: variables });
    const data = res.data ?? {};
    return { data: { id: data.key ?? id, ...data } };
  }) as DataProvider['update'],

  create: () => Promise.reject(new Error('create not implemented')),
  deleteOne: () => Promise.reject(new Error('deleteOne not implemented')),

  getMany: (async ({ resource, ids }: { resource: string; ids: (string | number)[] }) => {
    if (resource !== 'settings') {
      return { data: [] };
    }
    const res = await settingsApi();
    const all = res.data ?? [];
    const wanted = ids.map(String);
    const data = all
      .filter((s) => wanted.includes(s.key ?? ''))
      .map((s) => ({ id: s.key ?? '', ...s }));
    return { data };
  }) as DataProvider['getMany'],

  custom: () => Promise.reject(new Error('custom not implemented')),
};
